import { useEffect, useState } from 'react'
import { communicationService } from '../services/communication'
import type { WhatsAppMessage } from '../../../types'
import { MessageSquare, RefreshCw, AlertTriangle } from 'lucide-react'

interface MessageHistoryProps {
  leadId: number
}

// Map WhatsApp delivery statuses to design-token colours
const STATUS_COLOR: Record<string, string> = {
  sent:      'var(--text-tertiary)',
  delivered: 'var(--info-text)',
  read:      'var(--success-text)',
  failed:    'var(--danger-text)',
}

export default function MessageHistory({ leadId }: MessageHistoryProps) {
  const [messages, setMessages]   = useState<WhatsAppMessage[]>([])
  const [loading, setLoading]     = useState(true)
  const [refreshing, setRefreshing] = useState(false)

  const load = async (silent = false) => {
    if (!silent) setLoading(true)
    else setRefreshing(true)
    try {
      const data = await communicationService.getLeadMessages(leadId)
      setMessages(Array.isArray(data) ? data : [])
    } catch {
      // Non-critical — fail silently
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }

  useEffect(() => { load() }, [leadId])

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
          <MessageSquare className="h-4 w-4" style={{ color: 'var(--success-text)' }} />
          WhatsApp Messages
          {messages.length > 0 && (
            <span className="text-sm font-normal" style={{ color: 'var(--text-tertiary)' }}>
              ({messages.length})
            </span>
          )}
        </h3>
        <button
          onClick={() => load(true)}
          disabled={refreshing}
          className="btn-icon"
          title="Refresh"
        >
          <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-6">
          <div className="spinner" />
        </div>
      ) : messages.length === 0 ? (
        <div className="empty-state py-6">
          <MessageSquare className="h-8 w-8 mb-2" style={{ color: 'var(--text-disabled)' }} />
          <p>No messages yet</p>
        </div>
      ) : (
        <>
          {/* Failed message banner */}
          {messages.some(m => m.status === 'FAILED') && (
            <div className="alert-danger mb-3">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              <span>
                {messages.filter(m => m.status === 'FAILED').length} message(s) failed to deliver — customer may not have received them. Consider calling or resending.
              </span>
            </div>
          )}
          <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
            {messages.map(msg => {
              const isOutbound = msg.direction === 'OUTBOUND'
              const isFailed   = msg.status === 'FAILED'
              const errors     = msg.extra_data?.delivery_errors ?? []
              return (
                <div
                  key={msg.id}
                  className={`flex ${isOutbound ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className="max-w-[75%] px-3 py-2 text-sm"
                    style={{
                      background:   isFailed ? 'var(--danger-bg)'        : isOutbound ? 'var(--brand-600)' : 'var(--surface-subtle)',
                      color:        isFailed ? 'var(--danger-text)'       : isOutbound ? '#fff'              : 'var(--text-primary)',
                      borderRadius: isOutbound ? '8px 2px 8px 8px'        : '2px 8px 8px 8px',
                      border:       isFailed ? '1px solid var(--danger-border)' : 'none',
                    }}
                  >
                    {isFailed && (
                      <div className="flex items-center gap-1 text-xs font-medium mb-1">
                        <AlertTriangle className="h-3 w-3" />
                        Delivery failed
                      </div>
                    )}
                    {msg.template_name && (
                      <p
                        className="text-xs mb-1 font-medium"
                        style={{ color: isFailed ? 'var(--danger-text)' : isOutbound ? 'rgba(255,255,255,0.75)' : 'var(--text-tertiary)' }}
                      >
                        {msg.template_name}
                      </p>
                    )}
                    {msg.media_url ? (
                      <div className="mt-1">
                        <img
                          src={msg.media_url}
                          alt="Customer attachment"
                          className="max-w-full rounded"
                          style={{ maxHeight: '200px', cursor: 'pointer' }}
                          onClick={() => window.open(msg.media_url!, '_blank')}
                        />
                        {msg.body && !msg.body.startsWith('[Image]') && (
                          <p className="whitespace-pre-wrap break-words mt-1">{msg.body}</p>
                        )}
                      </div>
                    ) : (
                      <p className="whitespace-pre-wrap break-words">
                        {msg.body || msg.button_reply || '—'}
                      </p>
                    )}
                    {errors.length > 0 && (
                      <div className="mt-1 text-xs space-y-0.5" style={{ opacity: 0.85 }}>
                        {errors.map((e, i) => (
                          <p key={i}>Error {e.code}: {e.title}{e.details ? ` — ${e.details}` : ''}</p>
                        ))}
                      </div>
                    )}
                    {isFailed && errors.length === 0 && (
                      <div className="mt-1 text-xs" style={{ opacity: 0.85 }}>
                        {msg.extra_data?.send_error ? (
                          <p>
                            {msg.extra_data.send_error.message
                              ? `${msg.extra_data.send_error.message}${msg.extra_data.send_error.error_data ? ` — ${msg.extra_data.send_error.error_data}` : ''}`
                              : `Send failed (HTTP ${msg.extra_data.send_error.http_status})`}
                          </p>
                        ) : (
                          <p>Error details unavailable — resend the message to see the reason.</p>
                        )}
                      </div>
                    )}
                    <div
                      className="flex items-center justify-between gap-2 mt-1 text-xs"
                      style={{ color: isFailed ? 'var(--danger-text)' : isOutbound ? 'rgba(255,255,255,0.6)' : 'var(--text-tertiary)' }}
                    >
                      <span>
                        {new Date(msg.created_at).toLocaleString(undefined, {
                          month: 'short', day: 'numeric',
                          hour: '2-digit', minute: '2-digit',
                        })}
                      </span>
                      {isOutbound && !isFailed && (
                        <span style={{ color: STATUS_COLOR[msg.status] ?? 'rgba(255,255,255,0.6)' }}>
                          {msg.status}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </>
      )}
    </div>
  )
}
